import { channelColors } from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'

export function isChannelType(value: unknown): value is ChannelType {
  return typeof value === 'string' && Object.hasOwn(channelColors, value)
}

export function isFacebookGroup(channel: {
  service: string
  serverUrl?: string | null
}): boolean {
  return (
    (channel.service === 'facebook' &&
      channel.serverUrl?.startsWith('https://www.facebook.com/groups')) ??
    false
  )
}

/**
 * Whether an Instagram channel is connected through Instagram login rather
 * than through a Facebook Page. Instagram only exposes mentions on the
 * Facebook-login API, so this is what makes the Mentions inbox unavailable
 * for a channel.
 */
export function isInstagramLoginChannel(channel: {
  service?: string | null
  apiVersion?: string | null
}): boolean {
  return (
    channel.service === 'instagram' && channel.apiVersion === 'instagramLogin'
  )
}

type ChannelLocationInfo = { location?: string | null }

type ChannelMetadataInfo = {
  ['__typename']?: string
  serverUrl?: string | null
}

type ChannelWithAdditionalInfo = {
  service: Service
  serverUrl?: string | null
  locationData?: ChannelLocationInfo | null
  metadata?: ChannelMetadataInfo | null
}

export function getChannelAdditionalInfo(
  channel: ChannelWithAdditionalInfo,
): string | null {
  const serverUrl = isFacebookGroup(channel) ? null : channel.serverUrl

  if (
    (channel.service === 'bluesky' &&
      channel.metadata?.['__typename'] === 'BlueskyMetadata') ||
    (channel.service === 'mastodon' &&
      channel.metadata?.['__typename'] === 'MastodonMetadata')
  ) {
    return channel.metadata.serverUrl ?? null
  }

  return channel.locationData?.location ?? serverUrl ?? null
}

const channelServiceMap: Record<Service, string> = {
  instagram: 'Instagram',
  threads: 'Threads',
  linkedin: 'LinkedIn',
  bluesky: 'Bluesky',
  facebook: 'Facebook',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  mastodon: 'Mastodon',
  pinterest: 'Pinterest',
  googlebusiness: 'Google Business Profile',
  twitter: 'Twitter / X',
  startPage: 'Start Page',
  substack: 'Substack',
  whatsapp: 'WhatsApp',
}

export function isService(service?: string | null): service is Service {
  return Object.keys(channelServiceMap).includes(service ?? '')
}

export const channelServices = Object.keys(channelServiceMap).filter(isService)

export function getChannelName(service: Service): string {
  return channelServiceMap[service]
}

export function normalizeNetworkName(service: string): string {
  if (isService(service)) return channelServiceMap[service]
  return service.charAt(0).toUpperCase() + service.slice(1)
}

export function getChannelColor(service: Service): string {
  return channelColors[service]
}
